import { createFileRoute, Link } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  PanelRightOpen,
} from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { formatEnumValue } from '@/lib/formatters'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { useCompany } from '@/hooks/use-companies'
import { useCompanyMetricLabelOptions } from '@/hooks/use-metrics'
import { useHasCapability } from '@/hooks/use-permissions'
import { CompanyDetailLayout } from '@/components/custom/companies/overview/company-detail-layout'
import {
  CompanyDetailTabs,
  type CompanyDetailTabValue,
  isTabValue,
} from '@/components/custom/companies/overview/company-detail-tabs'
import {
  CompanySidePanel,
  CompanySidePanelSheet,
  SIDE_PANEL_WIDTH,
} from '@/components/custom/companies/overview/company-side-panel'
import { FinancialsTab } from '@/components/custom/companies/financials/tab'
import { OverviewTab } from '@/components/custom/companies/overview/tab'
import { ScorecardTab } from '@/components/custom/scorecard/scorecard-tab'
import { isScorecardEnabled } from '@/configs/feature-flags'
import type { Company } from '@/types/api'

type CompanyDetailSearch = {
  fromViewId?: string
  tab?: CompanyDetailTabValue
  trailingPeriod?: 'LTM' | 'MONTHLY' | 'QUARTERLY' | 'YTD'
  view?: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'
}

const VALID_TRAILING_PERIODS = ['LTM', 'MONTHLY', 'QUARTERLY', 'YTD'] as const
const VALID_VIEWS = ['MONTHLY', 'QUARTERLY', 'ANNUAL'] as const

export const Route = createFileRoute('/_authenticated/companies/$companyId')({
  component: CompanyDetailPage,
  validateSearch: (search: Record<string, unknown>): CompanyDetailSearch => ({
    fromViewId:
      typeof search.fromViewId === 'string' ? search.fromViewId : undefined,
    tab:
      typeof search.tab === 'string' && isTabValue(search.tab)
        ? search.tab
        : undefined,
    trailingPeriod:
      typeof search.trailingPeriod === 'string' &&
      (VALID_TRAILING_PERIODS as readonly string[]).includes(
        search.trailingPeriod
      )
        ? (search.trailingPeriod as CompanyDetailSearch['trailingPeriod'])
        : undefined,
    view:
      typeof search.view === 'string' &&
      (VALID_VIEWS as readonly string[]).includes(search.view)
        ? (search.view as CompanyDetailSearch['view'])
        : undefined,
  }),
})

function CompanyDetailPage() {
  const { companyId } = Route.useParams()
  const { fromViewId, tab, trailingPeriod, view } = Route.useSearch()
  const navigate = Route.useNavigate()

  const { data: company, isLoading, isError, refetch } = useCompany(companyId)
  const canEditScorecard = useHasCapability('company:view:all')

  // Kick off the metric-label-options fetch in parallel with the company
  // request. `FinancialStatCards` reuses the cached result via the same
  // React Query key — without this prefetch the labels request only
  // starts after `useCompany` resolves, which sequentially gates the
  // heaviest call on the detail page.
  useCompanyMetricLabelOptions(companyId)

  const scorecardEnabled = isScorecardEnabled()
  const activeTab =
    !scorecardEnabled && tab === 'kpis' ? 'overview' : (tab ?? 'overview')
  const isScorecardTab = activeTab === 'kpis'

  if (isLoading) {
    return (
      <CompanyDetailLayout
        sidePanel={
          <div className="hidden h-full lg:block">
            <CompanyDetailSidePanelSkeleton />
          </div>
        }
      >
        <div className="space-y-4 px-3 pb-8 pt-12 sm:px-4">
          <Skeleton className="h-5 w-60" />
          <Skeleton className="h-12 w-80" />
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-56 rounded-sm" />
            ))}
          </div>
          <Skeleton className="h-80 w-full rounded-sm" />
          <Skeleton className="h-96 w-full rounded-sm" />
        </div>
      </CompanyDetailLayout>
    )
  }

  if (isError || !company) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-primary/15 bg-white px-8 py-10">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <Typography variant="h5">Failed to load company details</Typography>
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <CompanyDetailLayout
      sidePanel={
        isScorecardTab ? undefined : (
          <div className="hidden h-full lg:block">
            <CompanySidePanel
              company={company}
              onRefresh={() => void refetch()}
            />
          </div>
        )
      }
    >
      <div className="space-y-4 px-3 pb-8 pt-12 sm:px-4">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <nav className="flex min-w-0 flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
            {fromViewId ? (
              <Link
                to="/companies/views/$viewId"
                params={{ viewId: fromViewId }}
                className="flex items-center gap-2 text-primary hover:text-primary/80"
              >
                <ArrowLeft className="size-4" />
                <Typography as="span" variant="body-sm">
                  Companies
                </Typography>
              </Link>
            ) : (
              <Link
                to="/companies"
                className="flex items-center gap-2 text-primary hover:text-primary/80"
              >
                <ArrowLeft className="size-4" />
                <Typography as="span" variant="body-sm">
                  Companies
                </Typography>
              </Link>
            )}
            <ChevronRight className="size-3.5 text-primary" />
            <Typography
              as="span"
              variant="body-sm"
              className="min-w-0 truncate font-semibold text-primary"
            >
              {company.displayName}
            </Typography>
          </nav>
          <CompanyDetailsButton
            company={company}
            onRefresh={() => void refetch()}
            showOnDesktop={isScorecardTab}
          />
        </div>

        <div className="border-b border-border pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="h1" className="break-words">
              {company.displayName}
            </Typography>
            <Badge variant={company.status === 'ACTIVE' ? 'green' : 'red'}>
              {formatEnumValue(company.status)}
            </Badge>
          </div>
        </div>

        <CompanyDetailTabs
          value={activeTab}
          showScorecard={scorecardEnabled}
          onValueChange={(next: CompanyDetailTabValue) => {
            navigate({
              search: (prev) => ({
                ...prev,
                tab: next === 'overview' ? undefined : next,
              }),
            })
          }}
        />

        <div
          id="company-detail-tabpanel"
          role="tabpanel"
          aria-labelledby={`company-detail-tab-${activeTab}`}
          tabIndex={0}
        >
          {activeTab === 'overview' ? (
            <OverviewTab company={company} />
          ) : activeTab === 'kpis' ? (
            <ScorecardTab
              surface="internal"
              companyId={company.id}
              fiscalYearEnd={company.fiscalYearEnd}
              canEdit={canEditScorecard}
            />
          ) : (
            <FinancialsTab
              companyId={company.id}
              initialTrailingPeriod={trailingPeriod}
              initialView={view}
            />
          )}
        </div>
      </div>
    </CompanyDetailLayout>
  )
}

function CompanyDetailsButton({
  company,
  onRefresh,
  showOnDesktop = false,
}: {
  company: Company
  onRefresh: () => void
  showOnDesktop?: boolean
}) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsDetailsOpen(true)}
        className={cn(
          'h-9 shrink-0 rounded-md border-primary/20 bg-white px-2.5 text-xs font-medium text-primary hover:bg-primary/5',
          showOnDesktop ? 'inline-flex' : 'lg:hidden'
        )}
      >
        <PanelRightOpen className="size-3.5" />
        Company details
      </Button>
      <CompanySidePanelSheet
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        company={company}
        onRefresh={onRefresh}
      />
    </>
  )
}

function CompanyDetailSidePanelSkeleton() {
  return (
    <div
      className={cn(
        SIDE_PANEL_WIDTH,
        'h-full shrink-0 border-l border-primary/50 bg-sidebar px-3 py-6'
      )}
    >
      <div className="space-y-3">
        <Skeleton className="h-32 w-full rounded-sm" />
        <Skeleton className="h-36 w-full rounded-sm" />
        <Skeleton className="h-28 w-full rounded-sm" />
        <Skeleton className="h-28 w-full rounded-sm" />
      </div>
    </div>
  )
}
